import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import {
  getLeiMunicipalAnexoI,
  getSiglaOrgao,
  getTenantMunicipio,
} from "../utils/tenantDocumentConfig";
import { deveExibirAssinaturaNaPagina } from "../utils/isencaoDocumentoPagina";

const SIG_BOX_X_PT = 72 * 2.83464567;
const SIG_BOX_Y_TOP_MM = 242;
const SIG_BOX_H_MM = 25;
const SIG_BOX_W_PT = 128 * 2.83464567;
const SIG_BOX_H_PT = SIG_BOX_H_MM * 2.83464567;
const SIG_BOX_BOTTOM_PT =
  (297 - (SIG_BOX_Y_TOP_MM + SIG_BOX_H_MM)) * 2.83464567;
const QR_LEFT_PT = 10 * 2.83464567;
const QR_WIDTH_PT = 60 * 2.83464567;

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 50,
    fontFamily: "Helvetica",
    fontSize: 10,
    lineHeight: 1.5,
  },
  headerImage: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  footerImage: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
  },
  titulo: {
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    textAlign: "center",
    marginBottom: 12,
  },
  abertura: {
    fontSize: 10,
    marginBottom: 16,
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  dataBlock: {
    marginBottom: 16,
  },
  dataLine: {
    fontSize: 10,
    marginBottom: 2,
  },
  dataLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
  },
  paragrafo: {
    fontSize: 10,
    textAlign: "justify",
    marginBottom: 12,
  },
  qrFixed: {
    position: "absolute",
    left: QR_LEFT_PT,
    bottom: SIG_BOX_BOTTOM_PT,
    width: QR_WIDTH_PT,
    height: SIG_BOX_H_PT,
    alignItems: "center",
    justifyContent: "center",
  },
  qrFixedImage: {
    width: 55,
    height: 55,
  },
  qrFixedLabel: {
    fontSize: 7,
    textAlign: "center",
    marginTop: 2,
  },
  signatureBoxFixed: {
    position: "absolute",
    left: SIG_BOX_X_PT,
    bottom: SIG_BOX_BOTTOM_PT,
    width: SIG_BOX_W_PT,
    height: SIG_BOX_H_PT,
  },
  signatureBox: {
    width: "100%",
    height: "100%",
    borderWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
    paddingTop: 3,
    paddingLeft: 6,
    paddingRight: 6,
    paddingBottom: 3,
  },
  signatureLabel: {
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  signatureImage: {
    marginTop: 2,
    width: SIG_BOX_W_PT / 2 - 10,
    height: SIG_BOX_H_PT - 18,
    objectFit: "contain",
  },
  signatureMessage: {
    fontSize: 7,
    marginTop: 6,
  },
});

const ESTADOS_POR_EXTENSO = {
  AC: "Acre",
  AL: "Alagoas",
  AP: "Amapá",
  AM: "Amazonas",
  BA: "Bahia",
  CE: "Ceará",
  DF: "Distrito Federal",
  ES: "Espírito Santo",
  GO: "Goiás",
  MA: "Maranhão",
  MT: "Mato Grosso",
  MS: "Mato Grosso do Sul",
  MG: "Minas Gerais",
  PA: "Pará",
  PB: "Paraíba",
  PR: "Paraná",
  PE: "Pernambuco",
  PI: "Piauí",
  RJ: "Rio de Janeiro",
  RN: "Rio Grande do Norte",
  RS: "Rio Grande do Sul",
  RO: "Rondônia",
  RR: "Roraima",
  SC: "Santa Catarina",
  SP: "São Paulo",
  SE: "Sergipe",
  TO: "Tocantins",
};

function estadoPorExtenso(uf) {
  if (!uf) return "";
  return ESTADOS_POR_EXTENSO[uf.trim().toUpperCase()] || uf;
}

function IsencaoLicenciamentoDocument({
  empreendimento,
  atividadeLabel,
  endereco,
  numeroProcesso,
  headerImgSrc,
  footerImgSrc,
  qrCodeDataUrl,
  assinaturaDataUrl,
  assinaturaMensagem,
}) {
  const nomeRazaoSocial = empreendimento?.nome || "Não informado";
  const cpfCnpj = empreendimento?.cnpj || "Não informado";
  const atividade = (atividadeLabel || "Não informado").toUpperCase();
  const enderecoCompleto = endereco || "Não informado";
  const { municipio: municipioNome, uf: estadoSigla } = getTenantMunicipio();
  const estadoNome = estadoPorExtenso(estadoSigla) || municipioNome;
  const siglaOrgao = getSiglaOrgao();
  const leiMunicipal = getLeiMunicipalAnexoI();

  const pageStyle = [
    styles.page,
    headerImgSrc ? { paddingTop: 80 } : {},
    { paddingBottom: footerImgSrc ? 200 : 160 },
  ];

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        {headerImgSrc && (
          <Image src={headerImgSrc} style={styles.headerImage} fixed />
        )}
        {footerImgSrc && (
          <Image src={footerImgSrc} style={styles.footerImage} fixed />
        )}

        <Text style={styles.titulo}>
          DECLARAÇÃO DE ISENÇÃO Nº {numeroProcesso || "—"}
        </Text>

        <Text style={styles.abertura}>
          O secretário da {siglaOrgao}, no uso de suas atribuições, expede à
        </Text>

        <View style={styles.dataBlock}>
          <Text style={styles.dataLine}>
            <Text style={styles.dataLabel}>Nome/Razão Social: </Text>
            {nomeRazaoSocial}
          </Text>
          <Text style={styles.dataLine}>
            <Text style={styles.dataLabel}>CNPJ/CPF: </Text>
            {cpfCnpj}
          </Text>
          <Text style={styles.dataLine}>
            <Text style={styles.dataLabel}>Endereço: </Text>
            {enderecoCompleto}
          </Text>
          <Text style={styles.dataLine}>
            <Text style={styles.dataLabel}>Município: </Text>
            {municipioNome}
          </Text>
        </View>

        <Text style={styles.paragrafo}>
          Declaramos para os devidos fins, que a atividade de{" "}
          <Text style={styles.bold}>{atividade}</Text>, de interesse da empresa{" "}
          <Text style={styles.bold}>{nomeRazaoSocial}</Text>, CNPJ{" "}
          <Text style={styles.bold}>{cpfCnpj}</Text>, situada{" "}
          <Text style={styles.bold}>{enderecoCompleto}</Text>, no município de{" "}
          {municipioNome}, estado do {estadoNome}, em nome do qual se emite a
          presente Declaração, não consta no Anexo I da lei municipal Nº{" "}
          {leiMunicipal} e nem na Resolução COEMA nº 02/2019, que lista as
          atividades passíveis de licenciamento ambiental. Portanto, embasada no ofício apresentado pelo interessado, a
          atividade, a ser realizada no município de {municipioNome}, no estado
          do {estadoNome}, está ISENTA de licenciamento ambiental perante à{" "}
          {siglaOrgao}.
        </Text>

        <Text style={styles.paragrafo}>
          O declarante afirma que as informações prestadas são autênticas e
          verídicas, responsabilizando-se por elas. Em caso de omissão de
          informação ou de falsa declaração, o declarante se sujeitará às sanções
          penais e administrativas cabíveis;
        </Text>

        <Text style={styles.paragrafo}>
          A presente declaração é concedida com base nas informações declaradas
          pelo interessado e não isenta o seu titular de requerer demais
          certidões, autorizações e congêneres a outros órgãos da Administração
          Pública conforme legislações correlatas; Para os casos em que haja
          mudança na atividade, esta poderá ser passível de licenciamento
          ambiental;
        </Text>

        <Text style={styles.paragrafo}>
          A presente declaração contempla apenas a atividade declarada pelo
          interessado, ficando o interessado ciente de que qualquer tipo de
          alteração e/ou ampliação do empreendimento pode ocorrer a necessidade
          de licenciamento ambiental, conforme a legislação;
        </Text>

        <Text style={styles.paragrafo}>
          Esta Declaração isenta do licenciamento ambiental a atividade acima
          mencionada, mas não autoriza supressão de vegetação, nem intervenção em
          Área de Preservação Permanente - APP, Unidades de Conservação da
          Natureza, terras indígenas administradas pela FUNAI, Quilombolas e/ou
          Assentamentos rurais (INCRA);
        </Text>

        <Text style={styles.paragrafo}>
          Deverão ser observadas as normas aplicáveis ao uso e ocupação do solo e
          o gerenciamento adequado dos resíduos sólidos e efluentes líquidos que
          venham a ser produzidos, de acordo com a legislação pertinente;
        </Text>

        <Text style={styles.paragrafo}>
          Esta declaração é válida por tempo indeterminado, possibilitando-se sua
          revisão e/ou revogação caso haja mudança na atividade apresentada;
        </Text>

        <Text style={styles.paragrafo}>
          A manifestação favorável da presente declaração não obsta a {siglaOrgao}{" "}
          de posteriores restrições ou indeferimento, para a atividade
          solicitada, considerando suas peculiaridades, e o desatendimento à
          legislação pertinente;
        </Text>

        {qrCodeDataUrl ? (
          <View
            fixed
            style={styles.qrFixed}
            render={({ pageNumber, totalPages }) =>
              deveExibirAssinaturaNaPagina(pageNumber, totalPages) ? (
                <View>
                  <Image src={qrCodeDataUrl} style={styles.qrFixedImage} />
                  <Text style={styles.qrFixedLabel}>Escaneie para validar</Text>
                </View>
              ) : null
            }
          />
        ) : null}

        <View
          fixed
          style={styles.signatureBoxFixed}
          render={({ pageNumber, totalPages }) =>
            deveExibirAssinaturaNaPagina(pageNumber, totalPages) ? (
              <View style={styles.signatureBox}>
                <Text style={styles.signatureLabel}>Assinatura Digital:</Text>
                {assinaturaDataUrl ? (
                  <Image src={assinaturaDataUrl} style={styles.signatureImage} />
                ) : assinaturaMensagem ? (
                  <Text style={styles.signatureMessage}>{assinaturaMensagem}</Text>
                ) : null}
              </View>
            ) : null
          }
        />
      </Page>
    </Document>
  );
}

export default IsencaoLicenciamentoDocument;
